import { useMemo, useState } from "react";
import { api, type CallFlow, type CallFlowSchedule } from "./api.ts";
import { flowIsAnswerable } from "./flow/layout.ts";

// Schedule manager — admins create rules saying "on these days,
// between X:XX and Y:YY, use flow Z". Lower priority = wins. The
// backend evaluates rules at every flow.conf re-render (every 5
// minutes via the boot-render timer + on every CRUD).
//
// UX: the source of truth is the coloured week grid — every minute of
// the week resolves to exactly one flow (rules first, fallback
// otherwise), mirroring chooseActiveFlow() server-side. The form and
// table below are just editors for what the grid shows.

const DAYS = [
  { mask: 1, short: "Lu", full: "Lundi" },
  { mask: 2, short: "Ma", full: "Mardi" },
  { mask: 4, short: "Me", full: "Mercredi" },
  { mask: 8, short: "Je", full: "Jeudi" },
  { mask: 16, short: "Ve", full: "Vendredi" },
  { mask: 32, short: "Sa", full: "Samedi" },
  { mask: 64, short: "Di", full: "Dimanche" },
];
const WEEKDAYS_MASK = 1 + 2 + 4 + 8 + 16;
const WEEKEND_MASK = 32 + 64;
const ALL_MASK = 127;

/** Stable per-flow colours: index in the flows list → palette entry. */
const FLOW_COLORS = ["#3b82f6", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444", "#06b6d4", "#f97316", "#84cc16"];

function hhmmToMin(s: string): number {
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return 0;
  return Math.min(1440, parseInt(m[1]!, 10) * 60 + parseInt(m[2]!, 10));
}

/** Mirror of the server's chooseActiveFlow cascade for one minute of
 *  one weekday: lowest priority matching rule wins, else fallback
 *  (returned with rule=null). Overnight ranges (end < start) wrap
 *  WITHIN the same weekday. */
function resolveAt(
  schedules: CallFlowSchedule[],
  fallbackId: string | null,
  dayBit: number,
  minute: number,
): { flowId: string | null; rule: CallFlowSchedule | null } {
  for (const r of [...schedules].sort((a, b) => a.priority - b.priority)) {
    if (!(r.weekdayMask & dayBit)) continue;
    const start = hhmmToMin(r.startsAt);
    const end = hhmmToMin(r.endsAt);
    const inside = end >= start ? minute >= start && minute < end : minute >= start || minute < end;
    if (inside) return { flowId: r.flowId, rule: r };
  }
  return { flowId: fallbackId, rule: null };
}

/** Current weekday (0=Mon) + minutes, in the PBX's timezone. */
function bxlNow(): { dow0Mon: number; minutes: number } {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Brussels",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
    const idx = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(get("weekday"));
    return {
      dow0Mon: idx < 0 ? 0 : idx,
      minutes: parseInt(get("hour") || "0", 10) * 60 + parseInt(get("minute") || "0", 10),
    };
  } catch {
    const d = new Date();
    return { dow0Mon: (d.getDay() + 6) % 7, minutes: d.getHours() * 60 + d.getMinutes() };
  }
}

export function FlowSchedule(props: {
  token: string;
  flows: CallFlow[];
  schedules: CallFlowSchedule[];
  onChange: () => void;
}) {
  const [flowId, setFlowId] = useState<string>("");
  const [days, setDays] = useState<number>(WEEKDAYS_MASK);
  const [startsAt, setStartsAt] = useState("09:00");
  const [endsAt, setEndsAt] = useState("17:00");
  /** Non-null = the form edits this existing rule instead of creating. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Load an existing rule into the form (click on a band or ✎). */
  function startEdit(rule: CallFlowSchedule): void {
    setEditingId(rule.id);
    setFlowId(rule.flowId);
    setDays(rule.weekdayMask);
    setStartsAt(rule.startsAt.slice(0, 5));
    setEndsAt(rule.endsAt.slice(0, 5));
    setError(null);
  }

  function cancelEdit(): void {
    setEditingId(null);
    setFlowId("");
    setDays(WEEKDAYS_MASK);
    setStartsAt("09:00");
    setEndsAt("17:00");
  }

  /** Click on a pale (fallback) zone: prefill a NEW rule covering it. */
  function prefillNew(dayMask: number, from: number, to: number): void {
    setEditingId(null);
    setDays(dayMask);
    setStartsAt(fmtMin(from));
    setEndsAt(fmtMin(to === 1440 ? 1439 : to));
    setError(null);
  }

  const colorOf = useMemo(() => {
    const map = new Map<string, string>();
    props.flows.forEach((f, i) => map.set(f.id, FLOW_COLORS[i % FLOW_COLORS.length]!));
    return (id: string | null): string => (id && map.get(id)) || "#cbd5e1";
  }, [props.flows]);

  const fallbackFlow = props.flows.find((f) => f.isDefault) ?? null;
  const now = bxlNow();
  const nowRes = resolveAt(props.schedules, fallbackFlow?.id ?? null, 1 << now.dow0Mon, now.minutes);
  const nowFlow = props.flows.find((f) => f.id === nowRes.flowId) ?? null;

  /** Per-day colour bands for the grid. Breakpoints = every rule
   *  boundary; each interval resolves through the same cascade the
   *  server uses, then adjacent same-outcome intervals merge. Each
   *  band remembers which rule produced it so a click can edit it. */
  const daySegments = useMemo(() => {
    return DAYS.map((d) => {
      const cuts = new Set<number>([0, 1440]);
      for (const r of props.schedules) {
        if (!(r.weekdayMask & d.mask)) continue;
        cuts.add(hhmmToMin(r.startsAt));
        cuts.add(hhmmToMin(r.endsAt));
      }
      const sorted = [...cuts].sort((a, b) => a - b);
      const segs: { from: number; to: number; flowId: string | null; ruleId: string | null }[] = [];
      for (let i = 0; i < sorted.length - 1; i++) {
        const from = sorted[i]!;
        const to = sorted[i + 1]!;
        const res = resolveAt(props.schedules, fallbackFlow?.id ?? null, d.mask, (from + to) / 2);
        const prev = segs[segs.length - 1];
        if (prev && prev.flowId === res.flowId && prev.ruleId === (res.rule?.id ?? null)) prev.to = to;
        else segs.push({ from, to, flowId: res.flowId, ruleId: res.rule?.id ?? null });
      }
      return segs;
    });
  }, [props.schedules, fallbackFlow?.id]);

  function toggleDay(mask: number): void {
    setDays((d) => (d & mask ? d & ~mask : d | mask));
  }

  async function save(): Promise<void> {
    if (!flowId || days === 0) return;
    setBusy(true);
    try {
      if (editingId) {
        await api.updateSchedule(props.token, editingId, {
          flowId,
          weekdayMask: days,
          startsAt,
          endsAt,
        });
      } else {
        // Priority is auto-assigned (new rule = evaluated after the
        // existing ones); nobody should have to think in numbers.
        const nextPriority =
          props.schedules.length === 0
            ? 100
            : Math.max(...props.schedules.map((s) => s.priority)) + 10;
        await api.createSchedule(props.token, {
          flowId,
          weekdayMask: days,
          startsAt,
          endsAt,
          priority: nextPriority,
        });
      }
      setError(null);
      cancelEdit();
      props.onChange();
    } catch (err) {
      setError(`${editingId ? "update" : "add"}: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    setBusy(true);
    try {
      await api.deleteSchedule(props.token, id);
      props.onChange();
    } catch (err) {
      setError(`delete: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /** Change the "tous les autres moments" fallback — only one flow
   *  can hold this slot at any time (partial unique index on
   *  call_flow.is_default enforces it server-side). */
  async function setFallback(newFlowId: string): Promise<void> {
    if (!newFlowId) return;
    setBusy(true);
    try {
      await api.patchFlow(props.token, newFlowId, { isDefault: true });
      props.onChange();
    } catch (err) {
      setError(`fallback: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const flowName = (id: string | null): string =>
    props.flows.find((f) => f.id === id)?.name ?? "(inconnu)";

  const GRID_H = 288; // 12 px per hour

  return (
    <section className="card">
      <div className="card-header">
        <h3>Semaine type — qui répond quand</h3>
      </div>
      {error && <div className="error">{error}</div>}

      {/* Legend + live status */}
      <div className="sched-legend">
        {props.flows.map((f) => (
          <span key={f.id} className="sched-legend__item">
            <span className="sched-legend__dot" style={{ background: colorOf(f.id) }} />
            {f.name}
            {f.isDefault && <span className="muted"> (repli)</span>}
          </span>
        ))}
        <span className="flex-spacer" />
        {nowFlow && (
          <span className="sched-legend__now" title="Selon les règles ci-dessous, hors activation manuelle">
            ▶ En ce moment : <strong style={{ color: colorOf(nowFlow.id) }}>{nowFlow.name}</strong>
          </span>
        )}
      </div>

      {/* Week grid */}
      <div className="sched-grid">
        <div className="sched-grid__hours" style={{ height: GRID_H }}>
          {[0, 6, 12, 18, 24].map((h) => (
            <span key={h} style={{ top: `${(h / 24) * 100}%` }}>{String(h).padStart(2, "0")}h</span>
          ))}
        </div>
        {DAYS.map((d, di) => (
          <div key={d.mask} className="sched-grid__day">
            <div className={`sched-grid__day-label${di === now.dow0Mon ? " is-today" : ""}`}>{d.short}</div>
            <div className="sched-grid__col" style={{ height: GRID_H }}>
              {[6, 12, 18].map((h) => (
                <div key={h} className="sched-grid__hline" style={{ top: `${(h / 24) * 100}%` }} />
              ))}
              {daySegments[di]!.map((seg, i) => {
                const isFallback = seg.ruleId === null;
                const isEditing = editingId !== null && seg.ruleId === editingId;
                const action = isFallback
                  ? "cliquez pour créer une règle sur ce créneau"
                  : "cliquez pour modifier cette règle";
                return (
                  <div
                    key={i}
                    className={`sched-grid__band${isEditing ? " is-editing" : ""}`}
                    title={`${d.full} ${fmtMin(seg.from)} → ${fmtMin(seg.to)} : ${flowName(seg.flowId)}${isFallback ? " (repli)" : ""} — ${action}`}
                    onClick={() => {
                      if (seg.ruleId) {
                        const rule = props.schedules.find((s) => s.id === seg.ruleId);
                        if (rule) startEdit(rule);
                      } else {
                        prefillNew(d.mask, seg.from, seg.to);
                      }
                    }}
                    style={{
                      top: `${(seg.from / 1440) * 100}%`,
                      height: `${((seg.to - seg.from) / 1440) * 100}%`,
                      background: colorOf(seg.flowId),
                      opacity: isFallback ? 0.28 : isEditing ? 1 : 0.9,
                    }}
                  />
                );
              })}
              {di === now.dow0Mon && (
                <div
                  className="sched-grid__now"
                  style={{ top: `${(now.minutes / 1440) * 100}%` }}
                  title={`Maintenant (${fmtMin(now.minutes)})`}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="muted" style={{ padding: "0.25rem", fontSize: 12, margin: 0 }}>
        Zones pâles = flux de repli « tous les autres moments ». Heures locales
        (Europe/Bruxelles) ; bascule effective à ±5 minutes. Une plage passant
        minuit (ex. 22:00 → 06:00) couvre le début et la fin des jours cochés.
      </p>

      {/* Add / edit rule form */}
      <div className={`sched-form${editingId ? " sched-form--editing" : ""}`}>
        <span className="sched-form__label">
          {editingId ? "✎ Modifier la règle :" : "Nouvelle règle :"}
        </span>
        <select value={flowId} onChange={(e) => setFlowId(e.target.value)} disabled={busy}>
          <option value="">— Quel flux ? —</option>
          {props.flows.map((f) => {
            const mute = !flowIsAnswerable(f.graph);
            return (
              <option key={f.id} value={f.id} disabled={mute}>
                {f.name}{mute ? " — muet (ne décroche pas)" : ""}
              </option>
            );
          })}
        </select>
        <div className="sched-form__days">
          {DAYS.map((d) => (
            <button
              key={d.mask}
              type="button"
              className={`sched-day${days & d.mask ? " is-on" : ""}`}
              onClick={() => toggleDay(d.mask)}
              title={d.full}
              disabled={busy}
            >
              {d.short}
            </button>
          ))}
          <button type="button" className="ghost sched-preset" onClick={() => setDays(WEEKDAYS_MASK)} disabled={busy}>
            Lun–Ven
          </button>
          <button type="button" className="ghost sched-preset" onClick={() => setDays(WEEKEND_MASK)} disabled={busy}>
            Week-end
          </button>
          <button type="button" className="ghost sched-preset" onClick={() => setDays(ALL_MASK)} disabled={busy}>
            7j/7
          </button>
        </div>
        <span className="sched-form__time">
          de <input type="time" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} disabled={busy} />
          {" "}à <input type="time" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} disabled={busy} />
        </span>
        <button className="primary" type="button" onClick={() => void save()} disabled={busy || !flowId || !days}>
          {editingId ? "💾 Enregistrer" : "+ Ajouter"}
        </button>
        {editingId && (
          <button className="ghost" type="button" onClick={cancelEdit} disabled={busy}>
            Annuler
          </button>
        )}
      </div>

      {/* Rules list */}
      <table className="data">
        <thead>
          <tr>
            <th>Flux</th>
            <th>Jours</th>
            <th>Plage</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {[...props.schedules]
            .sort((a, b) => a.priority - b.priority)
            .map((s) => {
              const active = nowRes.rule?.id === s.id;
              const editing = editingId === s.id;
              return (
                <tr
                  key={s.id}
                  className={editing ? "sched-row--editing" : active ? "sched-row--active" : undefined}
                >
                  <td>
                    <span className="sched-legend__dot" style={{ background: colorOf(s.flowId) }} />{" "}
                    {flowName(s.flowId)}
                    {active && <span className="muted"> — en cours</span>}
                    {editing && <span className="muted"> — en édition ci-dessus</span>}
                  </td>
                  <td>{formatDays(s.weekdayMask)}</td>
                  <td>
                    {s.startsAt.slice(0, 5)} → {s.endsAt.slice(0, 5)}
                    {hhmmToMin(s.endsAt) < hhmmToMin(s.startsAt) && (
                      <span className="muted" title="Plage passant minuit"> 🌙</span>
                    )}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="ghost" type="button" onClick={() => startEdit(s)} disabled={busy}>
                      ✎ Modifier
                    </button>
                    <button className="ghost" type="button" onClick={() => void remove(s.id)} disabled={busy}>
                      ✕ Supprimer
                    </button>
                  </td>
                </tr>
              );
            })}
          {/* Permanent fallback row — exactly one "tous les autres
              moments" slot; switching the flow moves is_default. */}
          <tr className="flow-schedule__fallback-row">
            <td>
              <select
                value={fallbackFlow?.id ?? ""}
                onChange={(e) => void setFallback(e.target.value)}
                disabled={busy}
              >
                <option value="" disabled>— Choisir un flux —</option>
                {props.flows.map((f) => {
                  const mute = !flowIsAnswerable(f.graph);
                  return (
                    <option key={f.id} value={f.id} disabled={mute}>
                      {f.name}{mute ? " — muet (ne décroche pas)" : ""}
                    </option>
                  );
                })}
              </select>
            </td>
            <td colSpan={2}>
              <span className="pill" style={{ background: "#ecfdf5", color: "#065f46", padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 500 }}>
                ⭐ Tous les autres moments
              </span>
            </td>
            <td style={{ textAlign: "right" }}>
              <span className="muted" style={{ fontSize: 11 }}>repli</span>
            </td>
          </tr>
        </tbody>
      </table>
      {props.schedules.length === 0 && (
        <p className="muted" style={{ padding: "0.5rem 0.25rem", fontSize: 12 }}>
          Aucune règle horaire — le flux de repli s'applique en permanence.
          Ajoutez une règle (ex. « WE répondeur », Week-end, 00:00 → 23:59)
          pour changer de flux à certains créneaux.
        </p>
      )}
    </section>
  );
}

function fmtMin(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** "Lun–Ven", "Week-end", "7j/7" when the mask matches a preset;
 *  otherwise the day list ("Lun, Mar, Jeu"). */
function formatDays(mask: number): string {
  if (mask === ALL_MASK) return "7j/7";
  if (mask === WEEKDAYS_MASK) return "Lun–Ven";
  if (mask === WEEKEND_MASK) return "Week-end";
  return DAYS.filter((d) => mask & d.mask).map((d) => d.short).join(", ");
}
